import React from 'react';
import { useAnomalyData, useAnomalyContext, useDismissAnomaly } from '../../hooks/useAnomalyData';
import { Panel, Badge } from '../../components/design';
import AnomalyDrawer from '../../components/anomalies/AnomalyDrawer';

const SEVERITY_COLORS = { critical: 'var(--danger)', high: '#f97316', warning: 'var(--warning)', unknown: 'var(--text-3)' };
const TYPE_LABELS = {
  threshold_breach: 'Threshold Breach', zscore_deviation: 'Z-Score Dev',
  bollinger_band_breach: 'Bollinger', sudden_spike: 'Spike', sudden_drop: 'Drop',
  increasing_trend: 'Trend ↑', decreasing_trend: 'Trend ↓', resource_saturation: 'Saturation',
};

export default function AnomaliesTab({ serverId }) {
  const [typeFilter, setTypeFilter] = React.useState('all');
  const [severityFilter, setSeverityFilter] = React.useState('all');
  const [page, setPage] = React.useState(1);
  const [selected, setSelected] = React.useState(null);
  const [refreshKey, setRefreshKey] = React.useState(0);
  const perPage = 20;

  const { anomaliesData, loading, statsData } = useAnomalyData({
    serverId, typeFilter, severityFilter, page, perPage, refreshKey,
  });

  const contextQ = useAnomalyContext(selected?.id);
  const dismissAnomaly = useDismissAnomaly();

  const anomalies = anomaliesData?.anomalies || [];
  const total = anomaliesData?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const stats = statsData || {};

  const handleDismiss = async (id, body) => {
    await dismissAnomaly(id, body);
    setRefreshKey((k) => k + 1);
  };

  return (
    <Panel title="Anomalies" sub={`${total} total`} noPad>
      <div className="filter-bar" style={{ margin: '16px 16px 0' }}>
        <select value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1); }}>
          <option value="all">All Types</option>
          {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={severityFilter} onChange={e => { setSeverityFilter(e.target.value); setPage(1); }}>
          <option value="all">All</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="warning">Warning</option>
        </select>
        {stats.total > 0 && (
          <div className="flex items-center gap-2" style={{ fontSize: 12 }}>
            <span className="muted"><span style={{ fontWeight: 600 }}>{stats.total}</span> total anomalies</span>
            {stats.by_severity && (
              <span className="flex items-center gap-1">
                {Object.entries(stats.by_severity).map(([sev, cnt]) => (
                  <Badge key={sev} tone={sev === 'critical' ? 'red' : sev === 'high' ? 'amber' : sev === 'warning' ? 'amber' : 'gray'}>{sev}:{cnt}</Badge>
                ))}
              </span>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <div className="muted" style={{ textAlign: 'center', padding: '40px 0' }}>Loading anomalies...</div>
      ) : anomalies.length === 0 ? (
        <div className="empty">
          <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-1)', marginBottom: 6 }}>
            {stats.total > 0 ? 'No anomalies matching filters' : 'No anomalies detected yet'}
          </h3>
        </div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th style={{ width: 144 }}>Time</th>
                <th>Type</th>
                <th>Metric</th>
                <th>Value</th>
                <th>Baseline</th>
                <th>Severity</th>
              </tr>
            </thead>
            <tbody>
              {anomalies.map((a, i) => (
                <tr
                  key={a.id || i}
                  onClick={() => setSelected(a)}
                  style={{ cursor: 'pointer' }}
                >
                  <td className="muted" style={{ fontSize: 12 }}>
                    {a.timestamp?.replace('T', ' ').substring(0, 19)}
                  </td>
                  <td>
                    <Badge tone="blue">{TYPE_LABELS[a.anomaly_type] || a.anomaly_type}</Badge>
                  </td>
                  <td className="mono" style={{ fontSize: 12 }}>{a.metric_name}</td>
                  <td className="mono" style={{ fontSize: 12 }}>
                    {a.actual_value != null ? Number(a.actual_value).toFixed(1) : '-'}
                  </td>
                  <td className="mono muted" style={{ fontSize: 12 }}>
                    {a.baseline_value != null ? Number(a.baseline_value).toFixed(1) : '-'}
                  </td>
                  <td>
                    <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 9999, marginRight: 4, background: SEVERITY_COLORS[a.severity] || 'var(--text-3)' }} />
                    <span className="muted" style={{ fontSize: 12 }}>{a.severity}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between" style={{ padding: '12px 16px', borderTop: '1px solid var(--glass-border)' }}>
          <span className="muted" style={{ fontSize: 12 }}>{total} anomalies</span>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
              className="chip" style={{ opacity: page <= 1 ? 0.3 : 1 }}>
              Prev
            </button>
            <span className="muted" style={{ fontSize: 12, padding: '0 8px' }}>{page}/{totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
              className="chip" style={{ opacity: page >= totalPages ? 0.3 : 1 }}>
              Next
            </button>
          </div>
        </div>
      )}

      <AnomalyDrawer
        anomaly={selected}
        context={contextQ.data}
        loading={contextQ.isLoading}
        error={contextQ.isError}
        onClose={() => setSelected(null)}
        onDismiss={handleDismiss}
        onSelect={setSelected}
      />
    </Panel>
  );
}
